<script lang="ts">
import { defineComponent, inject, ref } from 'vue'
import type { PropType } from 'vue'
import { type NavItem, type LinkNavItem, type DropdownNavItem } from '@/config'
import i18n from "@/i18n";
import store from '@/store'
import { isConfigurationFeatureEnabled } from '@/utils/configuration-feature-flags'

function isLink(item: NavItem): item is LinkNavItem {
  return 'href' in item
}

function isDropdown(item: NavItem): item is DropdownNavItem {
  return 'items' in item
}

function hasLinkOpened(item: NavItem, currentPath: string): boolean {
  if (isLink(item)) {
    return item.href === currentPath
  } else if (isDropdown(item)) {
    return item.items.some((child) => hasLinkOpened(child, currentPath))
  }
  return false
}

function isAuthorized(item: NavItem): boolean {
  if (isLink(item)) {
    if (item.featureFlag && !isConfigurationFeatureEnabled(item.featureFlag)) {
      return false;
    }
    if (!item.permissions || item.permissions.length === 0) {
      return true; // No permissions required, show the item
    }
    const userAuthorities = store.state.authModule?.user.authorities || [];

    const isOk = item.permissions.some((role: any) => userAuthorities.some((authority: any) => role.includes(authority.authority)));
    return isOk;
  }

  return item.items.some(isAuthorized);
}

export default defineComponent({
  name: 'NavItem',

  props: {
    item: {
      type: Object as PropType<NavItem>,
      required: true,
    }
  },

  setup(props) {
    const currentPath = window.location.pathname;
    const open = ref(hasLinkOpened(props.item, currentPath));
    // Etat replie du menu : fourni par App.vue, seul le 1er niveau porte une icone.
    const sidebarMini = inject('sidebarMini', ref(false));
    const toggle = () => {
      open.value = !open.value
    }

    return {
      i18n,
      open,
      currentPath,
      sidebarMini,
      toggle,
      isLink,
      isDropdown,
      hasLinkOpened,
      isAuthorized
    }
  },
})
</script>

<template>
  <!-- Link item -->
  <q-item v-if="isLink(item) && isAuthorized(item)" :to="(item as any).href" class="text-grey-6 tw-sidebar-btn relative"
    clickable>
    <q-item-section v-if="item.icon" avatar>
      <q-icon :name="item.icon" />
    </q-item-section>

    <q-item-section>
      <q-item-label>{{ i18n.global.t(item.label) }}</q-item-label>
    </q-item-section>

    <!-- Menu replie : le libelle est masque, l'infobulle le restitue -->
    <q-tooltip v-if="sidebarMini && item.icon" anchor="center right" self="center left" :offset="[8, 0]"
      class="tw-side-nav-tooltip">
      {{ i18n.global.t(item.label) }}
    </q-tooltip>
  </q-item>

  <!-- Dropdown item -->
  <template v-else-if="isDropdown(item) && isAuthorized(item)">
    <q-item clickable @click="toggle" class="text-grey-6 tw-sidebar-btn">
      <q-item-section v-if="item.icon" avatar>
        <q-icon :name="item.icon" />
      </q-item-section>

      <q-item-section>
        <q-item-label>{{ i18n.global.t(item.label) }}</q-item-label>
      </q-item-section>

      <q-item-section side>
        <q-icon :name="open ? 'expand_less' : 'expand_more'" />
      </q-item-section>

      <q-tooltip v-if="sidebarMini && item.icon" anchor="center right" self="center left" :offset="[8, 0]"
        class="tw-side-nav-tooltip">
        {{ i18n.global.t(item.label) }}
      </q-tooltip>
    </q-item>

    <q-slide-transition v-show="open">
      <q-list dense class="tw-side-bar-mernu-expanded-items">
        <NavItem v-for="(child, index) in (item as any).items" :key="index" :item="child" />
      </q-list>
    </q-slide-transition>
  </template>
</template>
